import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, UserPlus } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { UploadSection } from "@/components/UploadSection";
import { ResultsSection } from "@/components/ResultsSection";
import { AccountDialog } from "@/components/AccountDialog";
import { usePersistedSession } from "@/hooks/use-persisted-session";
import { searchBook } from "@/lib/api";
import { getAccountStatus, placeHold, cnFromUrl } from "@/lib/account";

const queryClient = new QueryClient();

function notFoundResult(book) {
  return {
    title: book.title, author: book.author,
    foundTitle: null, foundAuthor: null,
    found: false, hasPhysical: false, hasEbook: false, hasLargePrint: false,
    beatleyAvailable: 0, beatleyTotal: 0, systemAvailable: 0, systemTotal: 0,
    holds: 0, catalogUrl: "", branches: [], formats: [], series: null,
  };
}

function Home() {
  const [booksToSearch, setBooksToSearch] = useState([]);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [checkedAt, setCheckedAt] = useState(null);

  const [accountStatus, setAccountStatus] = useState({ configured: false, patronName: null });
  const [accountOpen, setAccountOpen] = useState(false);

  const { savedSession, persist, clear } = usePersistedSession();

  const checkedAtRef = useRef(null);
  const booksRef = useRef([]);

  useEffect(() => { checkedAtRef.current = checkedAt; }, [checkedAt]);
  useEffect(() => { booksRef.current = booksToSearch; }, [booksToSearch]);

  useEffect(() => {
    getAccountStatus()
      .then(setAccountStatus)
      .catch((e) => console.error("Failed to load account status:", e));
  }, []);

  const handleCsvUpload = (books) => {
    setBooksToSearch(books);
    setResults([]);
    setProgress(0);
    setCheckedAt(null);
  };

  const handleResume = () => {
    if (!savedSession) return;
    setBooksToSearch(savedSession.booksToSearch);
    setResults(savedSession.results);
    setProgress(savedSession.progress);
    setCheckedAt(savedSession.checkedAt);
  };

  const handleStartSearch = async () => {
    const startedAt = new Date().toISOString();
    setCheckedAt(startedAt);
    checkedAtRef.current = startedAt;
    setIsSearching(true);
    setProgress(0);
    setResults([]);

    const concurrency = 3;
    let i = 0;
    let localResults = [];
    let localProgress = 0;

    const runWorker = async () => {
      while (true) {
        const currentIndex = i++;
        if (currentIndex >= booksRef.current.length) break;
        const book = booksRef.current[currentIndex];
        let result;
        try {
          result = await searchBook(book);
        } catch {
          result = notFoundResult(book);
        }
        localResults = [...localResults, result];
        localProgress += 1;
        setResults([...localResults]);
        setProgress(localProgress);
        persist({
          booksToSearch: booksRef.current,
          results: localResults,
          progress: localProgress,
          checkedAt: checkedAtRef.current ?? startedAt,
        });
      }
    };

    const workers = [];
    for (let w = 0; w < concurrency; w++) workers.push(runWorker());
    await Promise.all(workers);
    setIsSearching(false);
  };

  const handleReset = () => {
    setBooksToSearch([]);
    setResults([]);
    setProgress(0);
    setCheckedAt(null);
    clear();
  };

  // Returns true if the hold was placed.
  const handlePlaceHold = async (catalogUrl) => {
    const cn = cnFromUrl(catalogUrl);
    if (!cn) {
      toast.error("No catalog record to place a hold on.");
      return false;
    }
    if (!accountStatus.configured) {
      toast.info("Connect your library account to place holds.");
      setAccountOpen(true);
      return false;
    }
    try {
      const res = await placeHold(cn);
      if (res.ok) {
        toast.success(res.message);
        return true;
      }
      toast.error(res.message || "Could not place the hold.");
      return false;
    } catch (err) {
      toast.error(err.message);
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8" data-testid="home-page">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex justify-end">
          <Button
            variant={accountStatus.configured ? "secondary" : "outline"}
            size="sm"
            className="gap-2"
            onClick={() => setAccountOpen(true)}
            data-testid="button-account"
          >
            {accountStatus.configured ? <ShieldCheck className="w-4 h-4 text-primary" /> : <UserPlus className="w-4 h-4" />}
            {accountStatus.configured
              ? `Connected: ${accountStatus.patronName || "Account"}`
              : "Connect Library Account"}
          </Button>
        </div>

        <div className="flex flex-col items-center justify-center text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground mb-4">
            Alexandria Library Checker
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            A quiet companion for your reading list. Upload your Goodreads "Want to Read" shelf and see what's waiting for you at the Beatley Central Library.
          </p>
        </div>

        {booksToSearch.length === 0 ? (
          <UploadSection onUpload={handleCsvUpload} savedSession={savedSession} onResume={handleResume} />
        ) : (
          <ResultsSection
            booksToSearch={booksToSearch}
            results={results}
            isSearching={isSearching}
            progress={progress}
            checkedAt={checkedAt}
            onStartSearch={handleStartSearch}
            onReset={handleReset}
            onPlaceHold={handlePlaceHold}
            accountConnected={accountStatus.configured}
          />
        )}
      </div>

      <AccountDialog
        open={accountOpen}
        onOpenChange={setAccountOpen}
        status={accountStatus}
        onStatusChange={setAccountStatus}
      />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        <Home />
      </div>
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}

export default App;
